/** One shared condition model for both Filter (single-path gate) and Branch (Yes/No fork).
 *  A condition is either a **field comparison** over an in-scope field, or an **event predicate**
 *  the engine watches for (e.g. the contact replied). Skeleton set — the real catalogue (typed
 *  operators per field, more events) comes from the API later. */

import { isDateFieldKey, isDaysAgoFieldKey } from './dataContext'
import { describeDateFilterValue, isReadableDateFilterValue } from './dateFilterValue'

type ValueOperator =
  | `is`
  | `is_not`
  | `contains`
  | `is_set`
  | `is_empty`
  | `greater_than`
  | `lesser_than`
  | `greater_equal_than`
  | `lesser_equal_than`
/** Only on `daysAgo` fields. The value is the whole number of days, never a `$now` expression. */
type DaysAgoOperator = `more_than_days_ago` | `less_than_days_ago`
export type FieldOperator = ValueOperator | DaysAgoOperator
// Outcome events like "replied" live on the action that produces them (the Wait-for-reply node),
// NOT here — a standalone condition can only test a queryable state, not the result of a send.
export type EventPredicate = `appointment_booked`
export type ConditionMatch = `all` | `any`

/** TRIGGER conditions carry a scope since the event-stream's change-vs-state split (PR #373):
 *  `change` = "this field just changed to X" (queried against the event's change data);
 *  `state` = "the record currently is X" (checked against the live record at enrollment).
 *  Branch/Filter conditions have no scope — steps always evaluate current state. */
export type ConditionScope = `change` | `state`

export type Condition =
  | { type: `field`; field?: string; operator: FieldOperator; value?: string; scope?: ConditionScope }
  | { type: `event`; event: EventPredicate; withinDays?: number }

/** Filter and Branch both carry this shape (alongside a `label`). */
export type ConditionGroup = { match: ConditionMatch; conditions: Condition[] }

/** The blank condition a new row starts as. */
export const NEW_CONDITION: Condition = { type: `field`, operator: `is` }

const FIELD_OPERATOR_OPTIONS: { value: ValueOperator; label: string; needsValue: boolean }[] = [
  { value: `is`, label: `is`, needsValue: true },
  { value: `is_not`, label: `is not`, needsValue: true },
  { value: `contains`, label: `contains`, needsValue: true },
  { value: `greater_than`, label: `greater than`, needsValue: true },
  { value: `greater_equal_than`, label: `at least`, needsValue: true },
  { value: `lesser_than`, label: `less than`, needsValue: true },
  { value: `lesser_equal_than`, label: `at most`, needsValue: true },
  { value: `is_set`, label: `is set`, needsValue: false },
  { value: `is_empty`, label: `is empty`, needsValue: false },
]

export const FIELD_OPERATORS = FIELD_OPERATOR_OPTIONS

export const DAYS_AGO_OPERATORS: readonly { value: DaysAgoOperator; label: string }[] = [
  { value: `more_than_days_ago`, label: `more than` },
  { value: `less_than_days_ago`, label: `less than` },
]

export const EVENT_PREDICATES: { value: EventPredicate; label: string }[] = [
  { value: `appointment_booked`, label: `Appointment booked` },
]

/** Row copy for the trigger-condition scope select — the sentence a user reads IS the semantics:
 *  "Lead status · just changed · is · Closed Lost" vs "Location · currently · is · Main St". */
export const CONDITION_SCOPE_OPTIONS: { value: ConditionScope; label: string }[] = [
  { value: `change`, label: `just changed` },
  { value: `state`, label: `currently` },
]

// Identity/contact-info fields default to "currently is" (people filter on what a record IS);
// lifecycle fields default to "changed to" (people automate on transitions).
const STATE_DEFAULT_FIELDS = new Set([
  `locationId`,
  `cellPhone`,
  `homePhone`,
  `workPhone`,
  `email`,
  `name`,
  `preferredName`,
  `tags`,
])

export function defaultConditionScope(field: string | undefined): ConditionScope {
  return field && STATE_DEFAULT_FIELDS.has(field) ? `state` : `change`
}

/** A trigger condition's effective scope — explicit choice, else the field-class default. */
export function conditionScope(condition: Condition): ConditionScope {
  if (condition.type !== `field`) return `change`
  return condition.scope ?? defaultConditionScope(condition.field)
}

export function operatorNeedsValue(operator: FieldOperator): boolean {
  return FIELD_OPERATOR_OPTIONS.find((option) => option.value === operator)?.needsValue ?? true
}

export function operatorLabel(operator: FieldOperator): string {
  return (
    [...FIELD_OPERATOR_OPTIONS, ...DAYS_AGO_OPERATORS].find((option) => option.value === operator)?.label ?? operator
  )
}

export function eventLabel(event: EventPredicate): string {
  return EVENT_PREDICATES.find((option) => option.value === event)?.label ?? event
}

/** A condition is complete when it can be evaluated. Events are always complete (the predicate is
 *  chosen by construction); a field comparison needs a field and (for value operators) a value.
 *
 *  A DATE field needs more than a non-empty value: the engine falls back to text ordering on a
 *  value it cannot read as a date, which matches every record instead of none (TRK-5617). An
 *  incomplete condition blocks Activate, which is the only thing standing between a mistyped
 *  `now-18y` and a workflow that quietly enrols the wrong people. */
export function conditionComplete(condition: Condition): boolean {
  if (condition.type === `event`) return true
  if (!condition.field) return false
  if (!operatorNeedsValue(condition.operator)) return true
  if (!condition.value?.trim()) return false
  if (isDaysAgoFieldKey(condition.field)) {
    return (
      DAYS_AGO_OPERATORS.some((option) => option.value === condition.operator) &&
      /^[1-9]\d*$/.test(condition.value.trim())
    )
  }
  return !isDateFieldKey(condition.field) || isReadableDateFilterValue(condition.value)
}

/** A pipeline-stage value carries its pipeline id (`<pipelineId>::<stage>`) because that is what the
 *  engine matches on. Nobody wants to read an id on a node card, so show the stage and let the
 *  drawer's picker carry the pipeline name. */
function readableValue(value: string, field?: string): string {
  if (isDaysAgoFieldKey(field)) return `${value} ${value === `1` ? `day` : `days`} ago`
  if (isDateFieldKey(field)) return describeDateFilterValue(value)
  const separator = value.indexOf(`::`)
  return separator === -1 ? value : value.slice(separator + 2)
}

/** One-line text for a condition, given a field-key → label resolver. */
export function conditionText(condition: Condition, fieldLabel: (key?: string) => string | undefined): string {
  if (condition.type === `event`) {
    const base = eventLabel(condition.event)
    return condition.withinDays ? `${base} within ${condition.withinDays}d` : base
  }
  if (!condition.field) return `Set a condition`
  const label = fieldLabel(condition.field) ?? condition.field
  const op = operatorLabel(condition.operator)
  return operatorNeedsValue(condition.operator)
    ? `${label} ${op}${condition.value ? ` ${readableValue(condition.value, condition.field)}` : ``}`
    : `${label} ${op}`
}

/** Node-card summary for a whole group. */
export function conditionGroupText(group: ConditionGroup, fieldLabel: (key?: string) => string | undefined): string {
  if (group.conditions.length === 0) return `No conditions yet`
  const first = conditionText(group.conditions[0], fieldLabel)
  if (group.conditions.length === 1) return first
  return `${first} +${group.conditions.length - 1} more (${group.match === `any` ? `any` : `all`})`
}
